import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { timeAgo } from '@/utils/dateFormat';
import type { Notification as LabNotification } from '@/types/index';

// Bell pulls from the same backend hook as the /notifications page so
// both views are coherent. Polling cadence (60s) and read-state writes
// are owned by the hook — see hooks/useNotifications.ts.

// Bug #322: unread rows are tinted, read rows fall back to plain white. The
// constant holding the *read* treatment used to be named
// `notificationUnreadStyle`, which read as though the condition were inverted,
// and the unread tint was a neutral grey rather than the magenta tint the
// /notifications page uses for the same state.
const UNREAD_TINT = '#faf8fb';

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  // Cap dropdown to the most recent 10; the full list lives at /notifications.
  const visibleNotifications = notifications.slice(0, 10);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const bellButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    color: 'var(--color-dark-grey)',
    transition: 'color 0.2s ease-in-out',
  };

  const badgeStyle: React.CSSProperties = {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    backgroundColor: '#dc3545',
    color: 'var(--color-white)',
    fontSize: '10px',
    fontWeight: 700,
    fontFamily: 'var(--font-family)',
    padding: '2px 6px',
    borderRadius: '10px',
    minWidth: '18px',
    textAlign: 'center',
    display: unreadCount > 0 ? 'block' : 'none',
  };

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    backgroundColor: 'var(--color-white)',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 200,
    minWidth: 320,
    maxWidth: 400,
    maxHeight: 400,
    display: isOpen ? 'flex' : 'none',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid var(--color-light-grey)',
    flexShrink: 0,
  };

  const headerTitleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'var(--font-family)',
    color: 'var(--color-black)',
    margin: 0,
  };

  const markAllLinkStyle: React.CSSProperties = {
    fontSize: '12px',
    fontFamily: 'var(--font-family)',
    color: 'var(--color-magenta)',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    textDecoration: 'none',
    transition: 'color 0.2s ease-in-out',
  };

  const listStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  };

  const notificationItemStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-light-grey)',
    backgroundColor: UNREAD_TINT,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out',
    fontSize: '13px',
    fontFamily: 'var(--font-family)',
  };

  const notificationReadStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-white)',
  };

  const badgeLabelStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    fontFamily: 'var(--font-family)',
    marginBottom: '4px',
    backgroundColor: 'var(--color-magenta)',
    color: 'var(--color-white)',
  };

  const titleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: 'var(--color-black)',
    margin: '4px 0',
  };

  const messageStyle: React.CSSProperties = {
    color: 'var(--color-dark-grey)',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    margin: '4px 0',
  };

  const timeStyle: React.CSSProperties = {
    color: '#999',
    fontSize: '11px',
    marginTop: '4px',
  };

  const footerStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderTop: '1px solid var(--color-light-grey)',
    textAlign: 'center',
    flexShrink: 0,
  };

  const footerLinkStyle: React.CSSProperties = {
    fontSize: '13px',
    fontFamily: 'var(--font-family)',
    color: 'var(--color-magenta)',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s ease-in-out',
  };

  const handleNotificationClick = (notification: LabNotification) => {
    if (!notification.isRead) {
      // Fire-and-forget — the hook handles the optimistic state update;
      // we don't want to block navigation on the round-trip.
      void markRead(notification.id);
    }
    if (notification.linkUrl) {
      navigate(notification.linkUrl);
    }
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        style={bellButtonStyle}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.color = 'var(--color-magenta)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.color = 'var(--color-dark-grey)';
        }}
      >
        <Bell size={24} />
        <span style={badgeStyle}>{unreadCount > 9 ? '9+' : unreadCount}</span>
      </button>

      <div style={panelStyle} ref={panelRef}>
        <div style={headerStyle}>
          <h3 style={headerTitleStyle}>Notifications</h3>
          {unreadCount > 0 && (
            <button
              style={markAllLinkStyle}
              onClick={() => void markAllRead()}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = 'var(--color-dark-magenta)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = 'var(--color-magenta)';
              }}
            >
              Mark all as read
            </button>
          )}
        </div>

        <div style={listStyle}>
          {visibleNotifications.length === 0 ? (
            <div
              style={{
                padding: '32px 16px',
                textAlign: 'center',
                color: 'var(--color-dark-grey)',
                fontSize: '13px',
                fontFamily: 'var(--font-family)',
              }}
            >
              No notifications
            </div>
          ) : (
            visibleNotifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  ...notificationItemStyle,
                  ...(notification.isRead ? notificationReadStyle : {}),
                }}
                onClick={() => handleNotificationClick(notification)}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    'var(--color-magenta-tint)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    notification.isRead ? 'var(--color-white)' : UNREAD_TINT;
                }}
              >
                {notification.sourceApp && (
                  <span style={badgeLabelStyle}>{notification.sourceApp}</span>
                )}
                <div style={titleStyle}>{notification.title}</div>
                {notification.message && (
                  <div style={messageStyle}>{notification.message}</div>
                )}
                <div style={timeStyle}>{timeAgo(notification.createdAt)}</div>
              </div>
            ))
          )}
        </div>

        <div style={footerStyle}>
          <a
            style={footerLinkStyle}
            onClick={() => {
              navigate('/notifications');
              setIsOpen(false);
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.color = 'var(--color-dark-magenta)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.color = 'var(--color-magenta)';
            }}
          >
            View all notifications
          </a>
        </div>
      </div>
    </div>
  );
};
